// ── Browser OTEL tracing setup ──────────────────────────────────────────────
// Initializes WebTracerProvider with OTLP HTTP exporter for SigNoz.
// Called ONCE by the host shell; MFEs inherit via singleton @opentelemetry/api.

import { trace, context } from '@opentelemetry/api'
import { Resource } from '@opentelemetry/resources'
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions'
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-web'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { ZoneContextManager } from '@opentelemetry/context-zone-peer-dep'

import type { BrowserTracingConfig } from '../types'

let _initialized = false

/**
 * Initialize browser OTEL tracing. Must be called once by the host shell.
 *
 * Sets up a WebTracerProvider with OTLP HTTP exporter, fetch instrumentation
 * (with trace propagation), document-load timing, and user interaction tracing.
 *
 * MFEs inherit tracing through the singleton `@opentelemetry/api` shared scope.
 */
export function initBrowserTracing(config: BrowserTracingConfig): void {
  if (_initialized) return
  _initialized = true

  const resource = Resource.default().merge(
    new Resource({
      [ATTR_SERVICE_NAME]: config.serviceName,
      ...config.resourceAttributes,
    }),
  )

  const exporter = new OTLPTraceExporter({
    url: config.collectorUrl,
  })

  const provider = new WebTracerProvider({
    resource,
    spanProcessors: [
      new BatchSpanProcessor(exporter, {
        maxQueueSize: 2048,
        maxExportBatchSize: 512,
        scheduledDelayMillis: 5000,
        exportTimeoutMillis: 30000,
      }),
    ],
  })

  provider.register({
    contextManager: new ZoneContextManager(),
  })

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: config.propagateTraceUrls ?? [/.*/],
        clearTimingResources: true,
      }),
      new DocumentLoadInstrumentation(),
      new UserInteractionInstrumentation({
        eventNames: ['click', 'submit'],
      }),
    ],
  })
}

/**
 * Get or create a tracer scoped to an MFE name.
 *
 * Returns a standard OTEL Tracer from the global TracerProvider.
 * Each MFE gets a distinct tracer name for span attribution.
 */
export function getTracer(mfeName: string) {
  return trace.getTracer(`@platform/observezen-react/${mfeName}`)
}
